import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import { ContactFormModal, draftKeyOf, type ContactFormValues } from "@/components/domains/ContactFormModal";
import {
  ErrorAlert,
  ErrorState,
  FullPageSpinner,
  Badge,
  Modal,
  Button,
  Breadcrumb,
  Toggle,
  useToast,
} from "@/components/ui";
import {
  ROLE_LABELS,
  domainKeys,
  domainsApi,
  longDate,
  verificationBadge,
  type Contact,
  type ContactImpact,
  type ContactUse,
} from "@/lib/domains";
import { portal } from "@/lib/api";
import { ApiError } from "@/lib/http";
import { DomainIcon, Hint } from "@/components/domains/parts";

const FIELD_LABELS: Record<string, string> = {
  first_name: "first name",
  last_name: "last name",
  organization_name: "organization",
  email: "email",
  phone: "phone",
  street: "street",
  city: "city",
  state: "state",
  postcode: "postcode",
  country: "country",
};

const fields = (names: string[]) => names.map((f) => FIELD_LABELS[f] ?? f).join(", ");

/** Domains, and the roles the contact has on each (a refusal may list the domains only). */
function Roles({ uses }: { uses: (Omit<ContactUse, "roles"> & { roles?: ContactUse["roles"] })[] }) {
  return (
    <ul className="flex flex-col gap-1.5">
      {uses.map((u) => (
        <li key={u.domain_id} className="flex flex-wrap items-center gap-2 text-sm">
          <Link to={`/domains/${u.domain_id}?tab=contacts`} className="font-semibold text-fg hover:underline">
            {u.fqdn}
          </Link>
          <span className="text-fg-subtle">{(u.roles ?? []).map((r) => ROLE_LABELS[r]).join(", ")}</span>
        </li>
      ))}
    </ul>
  );
}

/** `/domains/contacts`: the workspace's registration contacts. */
export function DomainContactsPage() {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const book = useQuery({
    queryKey: [...domainKeys.all(orgId), "contact-book", showArchived],
    queryFn: () => domainsApi.contactBook(orgId, showArchived),
    // A change the registries are still applying settles by itself: look again until it has.
    refetchInterval: (q) => (q.state.data?.contacts.some((c) => c.pending) ? 10_000 : false),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
  const toast = useToast();
  const create = useMutation({
    mutationFn: (v: ContactFormValues) => domainsApi.createContact(orgId, v),
    onSuccess: async () => {
      await refresh();
      setAdding(false);
      toast.show({ tone: "success", title: "Contact added" });
    },
  });
  const archive = useMutation({
    mutationFn: (c: Contact) =>
      c.archived_at ? domainsApi.unarchiveContact(orgId, c.id) : domainsApi.archiveContact(orgId, c.id),
    onSuccess: async (_, c) => {
      await refresh();
      toast.show({ tone: "success", title: c.archived_at ? "Contact restored" : "Contact archived" });
    },
  });

  if (book.isPending) return <FullPageSpinner />;
  if (book.isError) return <ErrorState error={book.error} onRetry={() => void book.refetch()} />;
  const contacts = book.data.contacts;
  const inUse =
    archive.error instanceof ApiError
      ? (archive.error.details.domains as ContactUse[] | undefined)
      : undefined;

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: "Domains", to: "/domains" }, { label: "Contacts" }]} />
      <div className="flex flex-wrap items-end gap-2.5">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="text-2xl font-bold text-fg">Contacts</h1>
          <p className="text-sm text-fg-subtle">
            The owners and contacts your domains are registered with. Registries require accurate details.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-fg-muted">
          Show archived
          <Toggle
            size="lg"
            checked={showArchived}
            onChange={setShowArchived}
            label="Show archived contacts"
          />
        </div>
        <Button variant="brand" size="lg" requires="domains:manage" onClick={() => setAdding(true)}>
          + Add contact
        </Button>
      </div>

      {archive.error ? (
        <div className="flex flex-col gap-2">
          <ErrorAlert error={archive.error} />
          {inUse && inUse.length > 0 && <Roles uses={inUse} />}
        </div>
      ) : null}

      {contacts.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-[18px] border border-border bg-surface px-6 py-14 text-center">
          <span className="flex size-11 items-center justify-center rounded-xl bg-[#faf6ef]">
            <DomainIcon name="globe" size={24} />
          </span>
          <p className="text-base font-semibold text-fg">
            {showArchived ? "No contacts" : "No saved contacts yet"}
          </p>
          <p className="max-w-md text-sm text-fg-subtle">
            Contacts you register domains with are kept here, so you can choose them again at checkout.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-[18px] border border-border bg-surface">
          {contacts.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-[18px] py-3.5">
              <div className="flex min-w-[220px] flex-1 flex-col gap-0.5">
                <p className="text-sm font-semibold text-fg">
                  {c.label ? `${c.label} · ` : ""}
                  {c.full_name}
                  {c.organization_name ? ` · ${c.organization_name}` : ""}
                </p>
                <p className="text-[13px] text-fg-subtle">
                  {c.email} · {c.address}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {c.archived_at && (
                  <Badge size="md" tone="neutral">
                    Archived
                  </Badge>
                )}
                {c.pending && (
                  <Badge size="md" tone="info">
                    Change pending
                  </Badge>
                )}
                <Badge size="md" tone={verificationBadge(c).tone}>
                  {verificationBadge(c).label}
                </Badge>
                <span className="text-[13px] text-fg-muted">
                  {c.domains_count ?? 0} {c.domains_count === 1 ? "domain" : "domains"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button size="lg" variant="secondary" onClick={() => setViewing(c.id)}>
                  View
                </Button>
                {!c.archived_at && (
                  <Button
                    size="lg"
                    variant="secondary"
                    requires="domains:manage"
                    disabledReason={
                      c.pending
                        ? "A change to this contact is waiting for the registry. Edit it once that's done."
                        : undefined
                    }
                    onClick={() => setEditing(c)}
                  >
                    Edit
                  </Button>
                )}
                <Button
                  size="lg"
                  variant="secondary"
                  requires="domains:manage"
                  loading={archive.isPending && archive.variables?.id === c.id}
                  onClick={() => archive.mutate(c)}
                >
                  {c.archived_at ? "Restore" : "Archive"}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Hint tone="warning" title="Archiving hides a contact, it doesn't delete it">
        An archived contact is no longer offered at checkout. The registrar keeps its own copy (it can&apos;t
        be deleted there), and your order history keeps the details each order was placed with.
      </Hint>

      <ContactFormModal
        key={adding ? "add" : "add-closed"}
        open={adding}
        onClose={() => setAdding(false)}
        title="Add a contact"
        subtitle="Saved for checkout. Nothing is sent to a registry until a domain is registered with it."
        submitLabel="Save contact"
        onSubmit={(v) => create.mutate(v)}
        pending={create.isPending}
        error={create.error}
      />
      {editing && <EditContact key={editing.id} contact={editing} onClose={() => setEditing(null)} />}
      <ContactDrawer contactId={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

type Step =
  | { kind: "form" }
  | { kind: "confirm"; values: ContactFormValues; impact: ContactImpact }
  | { kind: "new_contact"; values: ContactFormValues; impact: ContactImpact }
  | { kind: "saved_new"; contact: Contact; impact: ContactImpact };

/** Edit a saved contact: see what it changes first, then confirm, or save it as a new contact when it has to be. */
function EditContact({ contact, onClose }: { contact: Contact; onClose: () => void }) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>({ kind: "form" });
  const toast = useToast();
  const done = async () => {
    // The edit is saved: forget the draft of it (the form only previewed the change).
    void portal.discardFormDraft(orgId, draftKeyOf(contact)).catch(() => undefined);
    await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
    toast.show({ tone: "success", title: "Contact saved" });
    onClose();
  };
  const change = useMutation({
    mutationFn: (v: { values: ContactFormValues; token?: string | null }) =>
      domainsApi.changeContact(orgId, contact.id, {
        ...v.values,
        expected_version: contact.version ?? 1,
        confirm_token: v.token ?? null,
      }),
    onSuccess: done,
  });
  const preview = useMutation({
    mutationFn: (values: ContactFormValues) => domainsApi.contactImpact(orgId, contact.id, values),
    onSuccess: (impact, values) => {
      if (impact.mode === "no_change") {
        if ((values.label ?? null) !== (contact.label ?? null)) change.mutate({ values });
        else onClose();
      } else if (impact.mode === "unused") change.mutate({ values });
      else if (impact.mode === "in_place") setStep({ kind: "confirm", values, impact });
      else setStep({ kind: "new_contact", values, impact });
    },
  });
  const saveNew = useMutation({
    mutationFn: (values: ContactFormValues) => domainsApi.createContact(orgId, values),
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
      if (step.kind === "new_contact") setStep({ kind: "saved_new", contact: saved, impact: step.impact });
    },
  });

  if (step.kind === "form")
    return (
      <ContactFormModal
        open
        onClose={onClose}
        title="Edit contact"
        subtitle="You'll see what the change affects before anything is saved."
        initial={contact}
        clearDraftOnSubmit={false}
        submitLabel="Review change"
        onSubmit={(v) => preview.mutate(v)}
        pending={preview.isPending || change.isPending}
        error={preview.error ?? change.error}
      />
    );

  if (step.kind === "confirm")
    return (
      <Modal
        open
        onClose={onClose}
        title={`Change this contact on ${step.impact.domains.length} ${step.impact.domains.length === 1 ? "domain" : "domains"}?`}
        subtitle={`New ${fields(step.impact.changed_fields)}. The registries update every domain below; it can take a minute.`}
        width={520}
        footer={
          <>
            <Button variant="secondary" size="lg" onClick={() => setStep({ kind: "form" })}>
              Back
            </Button>
            <Button
              variant="brand"
              size="lg"
              loading={change.isPending}
              onClick={() => change.mutate({ values: step.values, token: step.impact.confirm_token })}
            >
              Change it
            </Button>
          </>
        }
      >
        <Roles uses={step.impact.domains} />
        <ErrorAlert error={change.error} />
      </Modal>
    );

  if (step.kind === "new_contact")
    return (
      <Modal
        open
        onClose={onClose}
        title="Save these details as a new contact"
        subtitle={
          step.impact.reason === "shared"
            ? "These details are also used by another account at the registry, so they can't be changed in place."
            : "A new name, organization or email for a domain's owner changes who owns it, so it isn't changed in place."
        }
        width={520}
        footer={
          <>
            <Button variant="secondary" size="lg" onClick={() => setStep({ kind: "form" })}>
              Back
            </Button>
            <Button
              variant="brand"
              size="lg"
              loading={saveNew.isPending}
              onClick={() => saveNew.mutate(step.values)}
            >
              Save as a new contact
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg-muted">
          Save the new details as a contact, then move each domain to it from the domain&apos;s Contacts tab.
          A new owner needs both the current and the new owner to approve by email. The current contact stays
          as it is.
        </p>
        <Roles uses={step.impact.domains} />
        <ErrorAlert error={saveNew.error} />
      </Modal>
    );

  return (
    <Modal
      open
      onClose={() => void done()}
      title="New contact saved"
      subtitle={`${step.contact.full_name} is in your contacts. Move each domain to it from its Contacts tab:`}
      width={520}
      footer={
        <Button variant="brand" size="lg" onClick={() => void done()}>
          Done
        </Button>
      }
    >
      <Roles uses={step.impact.domains} />
    </Modal>
  );
}

/** Where the owner must confirm their e-mail: the deadline, the domains it covers, or why it is settled. */
function Verification({ contact }: { contact: Contact }) {
  const badge = verificationBadge(contact);
  const domains = contact.verification_domains ?? [];
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-semibold text-fg">Email verification</p>
      <div className="flex flex-wrap items-center gap-2 text-sm text-fg-muted">
        <Badge size="md" tone={badge.tone}>
          {badge.label}
        </Badge>
        {badge.detail && <span>{badge.detail}</span>}
      </div>
      {contact.verification_required ? (
        contact.verification === "pending" && (
          <p className="text-sm text-fg-subtle">
            {contact.verification_sent_at
              ? `Asked on ${longDate(contact.verification_sent_at)}. `
              : "Asked once a domain is registered with it. "}
            For {domains.join(", ")}: the registry may suspend {domains.length === 1 ? "it" : "them"} until{" "}
            {contact.email} is confirmed.
          </p>
        )
      ) : (
        <p className="text-sm text-fg-subtle">
          None of the domains it owns has a registry that asks the owner to confirm their email.
        </p>
      )}
    </div>
  );
}

function ContactDrawer({ contactId, onClose }: { contactId: string | null; onClose: () => void }) {
  const { orgId } = useOrganization();
  const detail = useQuery({
    queryKey: [...domainKeys.all(orgId), "contact", contactId],
    queryFn: () => domainsApi.contactDetail(orgId, contactId!),
    enabled: Boolean(contactId),
  });
  const d = detail.data;
  return (
    <Modal
      open={Boolean(contactId)}
      onClose={onClose}
      title={d ? d.contact.full_name : "Contact"}
      subtitle={d ? `${d.contact.email} · ${d.contact.address}` : undefined}
      width={560}
    >
      {detail.isPending ? (
        <p className="text-sm text-fg-subtle">Loading…</p>
      ) : detail.isError ? (
        <ErrorAlert error={detail.error} />
      ) : d ? (
        <div className="flex flex-col gap-5">
          {d.shared && (
            <Hint tone="warning" title="Shared at the registry">
              Another account at the registry uses exactly these details, so changes are saved as a new
              contact.
            </Hint>
          )}
          <Verification contact={d.contact} />
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-fg">Domains</p>
            {d.domains.length ? (
              <Roles uses={d.domains} />
            ) : (
              <p className="text-sm text-fg-subtle">No domain uses it.</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-fg">History</p>
            <ul className="flex flex-col gap-1 text-sm text-fg-muted">
              {d.history.map((e, i) => (
                <li key={`${e.at}-${i}`}>
                  {longDate(e.at)} · {e.action.replace(/_/g, " ")}
                  {e.changed_fields.length ? ` (${fields(e.changed_fields)})` : ""}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
